import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Package,
  Truck,
  FileText,
  QrCode,
  Grid,
  CheckCircle,
  RefreshCw,
  Box,
  Clipboard,
  ChevronRight,
  Printer,
  Scan,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color, onClick, testId }) => (
  <div 
    onClick={onClick}
    data-testid={testId}
    className={`bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all ${onClick ? 'cursor-pointer hover:border-[#2b3380]/30' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
        {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
      </div>
      <div className={`p-3 rounded-xl ${color || 'bg-blue-50'}`}>
        <Icon className={`w-6 h-6 ${color?.includes('purple') ? 'text-purple-600' : color?.includes('green') ? 'text-green-600' : color?.includes('orange') ? 'text-orange-600' : 'text-blue-600'}`} />
      </div>
    </div>
  </div>
);

// Action Card Component
const ActionCard = ({ icon: Icon, title, description, onClick, badge, testId }) => (
  <div 
    onClick={onClick}
    data-testid={testId}
    className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-[#2b3380]/30 transition-all cursor-pointer group"
  >
    <div className="flex items-center gap-4">
      <div className="p-3 bg-[#2b3380]/5 rounded-xl group-hover:bg-[#2b3380]/10 transition-colors">
        <Icon className="w-6 h-6 text-[#2b3380]" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          {badge > 0 && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
              {badge}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#2b3380] transition-colors" />
    </div>
  </div>
);

// Pending Item Row
const PendingItem = ({ type, id, details, time, onClick }) => {
  const typeConfig = {
    manifest: { icon: Truck, color: 'text-purple-600 bg-purple-50' },
    grn: { icon: FileText, color: 'text-blue-600 bg-blue-50' },
    did: { icon: QrCode, color: 'text-orange-600 bg-orange-50' },
    stn: { icon: Package, color: 'text-green-600 bg-green-50' }
  };
  
  const config = typeConfig[type] || typeConfig.manifest;
  const IconComp = config.icon;
  
  return (
    <div 
      onClick={onClick}
      className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
    >
      <div className={`p-2 rounded-lg ${config.color}`}>
        <IconComp className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{id}</p>
        <p className="text-xs text-gray-500 truncate">{details}</p>
      </div>
      <span className="text-xs text-gray-400">{time}</span>
    </div>
  );
};

function WarehouseDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingManifests, setPendingManifests] = useState([]);
  const [pendingGRN, setPendingGRN] = useState([]);
  const [iqcPending, setIqcPending] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, manifestsRes, grnRes, iqcRes] = await Promise.all([
        fetch(`${API_URL}/api/warehouse/dashboard/summary`, { headers }),
        fetch(`${API_URL}/api/warehouse/manifest/pending`, { headers }),
        fetch(`${API_URL}/api/warehouse/grn/pending`, { headers }),
        fetch(`${API_URL}/api/warehouse/devices/iqc-pending`, { headers })
      ]);

      const summary = await summaryRes.json();
      const manifests = await manifestsRes.json();
      const grn = await grnRes.json();
      const iqc = await iqcRes.json();

      setDashboardData(summary);
      setPendingManifests(manifests.manifests || []);
      setPendingGRN(grn.grns || []);
      setIqcPending(iqc.devices || []);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const stats = dashboardData || {};
  const manifestStats = stats.manifest_stats || {};
  const deviceStats = stats.device_stats || {};
  const stnStats = stats.stn_stats || {};
  const todayStats = stats.today || {};

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Warehouse Operations</h1>
            <p className="text-sm text-gray-500">Buyback device management & processing</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-gray-500 hover:text-[#2b3380] hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/warehouse/grn')}
              className="px-4 py-2 bg-[#2b3380] text-white rounded-lg font-medium hover:bg-[#232a66] transition-colors flex items-center gap-2"
            >
              <Scan className="w-4 h-4" />
              Start GRN
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380]"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={Truck}
                label="Manifests In-Transit"
                value={manifestStats.in_transit || 0}
                subValue="Awaiting arrival"
                color="bg-purple-50"
                onClick={() => navigate('/warehouse/manifests')}
                testId="stat-manifests-in-transit"
              />
              <StatCard
                icon={FileText}
                label="GRN Pending"
                value={manifestStats.pending_grn || 0}
                subValue="Ready for receiving"
                color="bg-blue-50"
                onClick={() => navigate('/warehouse/grn')}
                testId="stat-grn-pending"
              />
              <StatCard
                icon={QrCode}
                label="DID Pending"
                value={stats.grn_pending_did || 0}
                subValue="Need label generation"
                color="bg-orange-50"
                testId="stat-did-pending"
              />
              <StatCard
                icon={Clipboard}
                label="IQC Queue"
                value={deviceStats.IQC_PENDING || 0}
                subValue="At TRC"
                color="bg-green-50"
                onClick={() => navigate('/trc/iqc')}
                testId="stat-iqc-queue"
              />
            </div>

            {/* Device Status Overview */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Device Status Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {Object.entries(deviceStats).filter(([_, count]) => count > 0).map(([status, count]) => (
                  <div key={status} className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xl font-bold text-gray-800">{count}</p>
                    <p className="text-xs text-gray-500">{status.replace(/_/g, ' ')}</p>
                  </div>
                ))}
                {Object.values(deviceStats).every(v => v === 0) && (
                  <div className="col-span-full text-center py-4 text-gray-400">
                    No devices in system yet
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Quick Actions */}
              <div className="lg:col-span-2 space-y-4">
                <h2 className="text-lg font-semibold text-gray-800">Quick Actions</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ActionCard
                    icon={Truck}
                    title="Buyback Manifests"
                    description="View & manage pickup manifests"
                    onClick={() => navigate('/warehouse/manifests')}
                    badge={pendingManifests.length}
                    testId="action-buyback-manifests"
                  />
                  <ActionCard
                    icon={FileText}
                    title="GRN Processing"
                    description="Receive devices & generate GRN"
                    onClick={() => navigate('/warehouse/grn')}
                    badge={manifestStats.pending_grn}
                    testId="action-grn-processing"
                  />
                  <ActionCard
                    icon={QrCode}
                    title="DID Label Printing"
                    description="Generate device labels"
                    onClick={() => navigate('/warehouse/did-labels')}
                    testId="action-did-labels"
                  />
                  <ActionCard
                    icon={Package}
                    title="STN to TRC"
                    description="Transfer devices to TRC"
                    onClick={() => navigate('/warehouse/stn')}
                    badge={stnStats.created}
                    testId="action-stn-trc"
                  />
                  <ActionCard
                    icon={Grid}
                    title="Bin Master"
                    description="Manage warehouse bins"
                    onClick={() => navigate('/warehouse/bin-master')}
                    testId="action-bin-master"
                  />
                  <ActionCard
                    icon={Printer}
                    title="Label Settings"
                    description="Configure DID label format"
                    onClick={() => navigate('/masters/label-config')}
                    testId="action-label-settings"
                  />
                </div>

                {/* Today's Activity */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mt-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Today's Activity</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#2b3380]">{todayStats.manifests_received || 0}</p>
                      <p className="text-sm text-gray-500">Manifests Received</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#2b3380]">{todayStats.grns_created || 0}</p>
                      <p className="text-sm text-gray-500">GRNs Created</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#2b3380]">{todayStats.dids_generated || 0}</p>
                      <p className="text-sm text-gray-500">DIDs Generated</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Items */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-800">Pending Items</h2>
                
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                  {pendingManifests.length > 0 && (
                    <div className="p-4 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Manifests</p>
                      {pendingManifests.slice(0, 3).map((m) => (
                        <PendingItem
                          key={m.manifest_id}
                          type="manifest"
                          id={m.manifest_id}
                          details={`${m.engineer_name} • ${m.lead_count} devices`}
                          time={m.status}
                          onClick={() => navigate(`/warehouse/manifests?id=${m.manifest_id}`)}
                        />
                      ))}
                    </div>
                  )}

                  {pendingGRN.length > 0 && (
                    <div className="p-4 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">GRN Pending DID</p>
                      {pendingGRN.slice(0, 3).map((g) => (
                        <PendingItem
                          key={g.grn_id}
                          type="did"
                          id={g.grn_id}
                          details={`${g.pending_did_count || g.received_count} devices need DID`}
                          time="Pending"
                          onClick={() => navigate(`/warehouse/grn?id=${g.grn_id}`)}
                        />
                      ))}
                    </div>
                  )}

                  {iqcPending.length > 0 && (
                    <div className="p-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">IQC Queue</p>
                      {iqcPending.slice(0, 3).map((d) => (
                        <PendingItem
                          key={d.device_id}
                          type="grn"
                          id={d.device_id}
                          details={`${d.brand} ${d.model}`}
                          time={d.pq_grade}
                          onClick={() => navigate(`/trc/iqc?did=${d.device_id}`)}
                        />
                      ))}
                    </div>
                  )}

                  {pendingManifests.length === 0 && pendingGRN.length === 0 && iqcPending.length === 0 && (
                    <div className="p-8 text-center">
                      <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                      <p className="text-gray-500">All caught up!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default WarehouseDashboard;
